/**
 * TextEditor — the "Type Configuration" panel for the `text` control.
 *
 * ── Why this file is new ───────────────────────────────────────────────────────────────────────
 *
 * `text` had **no** registered `PropertyEditor` before Plan/05 §3B. `PropertiesTab`'s `ControlConfig`
 * does a bare `PropertyEditorRegistry.get(control.type) || DefaultPropertyEditor`, so selecting a
 * text field produced "No specialized configuration for **text**" plus a read-only JSON dump of
 * `control.config`. (Note the `{ type: 'text', … render: TextPropertyEditor }` block in
 * `registry/EditorLoader.ts` is **not** a counter-example — `loadBaseEditors()` is defined but never
 * called anywhere, and the comment in `initializeEditors` says that is deliberate: those base
 * editors are UI atoms, and registering them would shadow the real control-type editors.)
 *
 * So the §3B toggle needed an editor to live in, and this is it. `maxLength` comes along because it
 * was at least *visible* in the fallback's JSON dump for any field that set one; replacing the
 * fallback without it would have removed information from the panel. Here it is editable rather than
 * merely visible.
 *
 * **The box shows no number when no cap is configured.** It used to render `?? 255`, which showed a
 * `255` that nothing enforced: `text` fields have no cap unless an author sets one, and the earlier
 * version of this comment justified the fabricated default by saying `getDefaultConfig('text')`
 * seeds `{ maxLength: 255 }`. It does contain that entry — but `getDefaultConfig` is never invoked
 * anywhere in this repo, so nothing seeds anything, and a panel that displays a limit which does not
 * exist (beside help text asserting it is enforced) is simply wrong. Empty + a "No limit"
 * placeholder is what is actually true.
 *
 * ── Why "Other settings" exists ────────────────────────────────────────────────────────────────
 *
 * Registering an editor for `text` *replaces* `DefaultPropertyEditor` entirely, and that fallback's
 * JSON dump was the only place several real config keys were ever visible: `autoComplete` and
 * `pattern` are both read by `FormField.tsx`'s `case 'text':`, and a schema may legitimately carry
 * custom keys a host app reads for itself. Shipping only "Max Length" + the toggle would therefore
 * have been a net *loss* of visibility for every text field with any other key set — a regression
 * introduced by an addition. `OtherConfigSection` below restores exactly that dump, collapsed by
 * default and filtered to the keys no dedicated field above already covers, so nothing that used to
 * be visible stopped being visible.
 *
 * `email`/`url`/`tel` deliberately get no editor and keep the fallback: they render through the same
 * `case` in `FormField.tsx` but the §3B gate checks `control.type === 'text'` specifically, so
 * offering them the toggle would produce a switch that does nothing (Plan/05 §3B's stated default
 * position — those are atomic values, not template content).
 */

import React, { useState } from 'react';
import type { FormControl } from '@atlas-forms/types-js';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';
import { ExpressionBuilderSection } from './ExpressionBuilderSection';
import { TEXT_EXPRESSION_DEFAULT_FORMAT } from '@atlas-forms/player-components-react';

/**
 * Keys this panel already exposes through a dedicated control, and which therefore must not be
 * repeated in the read-only "Other settings" dump — seeing `maxLength` twice, once editable and once
 * as JSON, reads as a bug.
 */
const COVERED_CONFIG_KEYS: ReadonlyArray<string> = ['maxLength', 'expressionBuilder'];

/**
 * The read-only `control.config` JSON dump `DefaultPropertyEditor` shows, restored for the keys this
 * panel does not itself expose — see this file's header for why registering an editor *removed* it.
 *
 * Collapsed by default, and absent entirely when there is nothing left over: an empty disclosure
 * always disappoints whoever opens it. Read-only on purpose — making arbitrary config keys editable
 * as free-text JSON is a different feature with its own validation questions, and the only
 * requirement here is that nothing became *less* visible than it was before this editor existed.
 */
function OtherConfigSection({ control }: { control: FormControl }): React.ReactElement | null {
  const [expanded, setExpanded] = useState(false);

  const remaining = Object.fromEntries(
    Object.entries(control.config ?? {}).filter(([key]) => !COVERED_CONFIG_KEYS.includes(key)),
  );
  const remainingKeys = Object.keys(remaining);
  if (remainingKeys.length === 0) return null;

  const contentID = `${control.id}-other-config`;

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">
        <button
          type="button"
          onClick={() => setExpanded((previous) => !previous)}
          aria-expanded={expanded}
          aria-controls={contentID}
          data-testid="af-other-config-toggle"
          className="af-properties-tab__disclosure"
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
            color: 'inherit',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
          Other settings ({remainingKeys.length})
        </button>
      </h4>

      {expanded && (
        <div className="af-properties-tab__group" id={contentID} data-testid="af-other-config-body">
          <label className="af-properties-tab__label">Current Config (JSON)</label>
          <pre
            className="af-properties-tab__input"
            style={{
              fontFamily: 'Consolas, Monaco, monospace',
              margin: 0,
              padding: '8px',
              borderRadius: '3px',
              fontSize: '12px',
              overflow: 'auto',
              maxHeight: '200px',
            }}
          >
            {JSON.stringify(remaining, null, 2)}
          </pre>
          <p className="af-properties-tab__help">
            Config keys with no dedicated field above. Read-only here — edit them in the form schema
            itself.
          </p>
        </div>
      )}
    </div>
  );
}

function renderTextEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;

  /**
   * Empty string, not a fabricated number, when the field genuinely has no cap — see the header.
   * A controlled `<input type="number">` needs `''` rather than `undefined` for "blank", or React
   * switches it to an uncontrolled input mid-life.
   */
  const configuredMaxLength = control.config?.maxLength as number | undefined;
  const maxLengthValue = typeof configuredMaxLength === 'number' ? configuredMaxLength : '';

  return (
    <>
      <div className="af-properties-tab__section">
        <h4 className="af-properties-tab__subsection-title">Control Configuration</h4>
        <div className="af-properties-tab__group">
          <label className="af-properties-tab__label">
            Max Length
            <input
              type="number"
              min="1"
              value={maxLengthValue}
              /*
               * Clearing the box removes the key rather than substituting a number. Writing a
               * fallback here would make "I cleared this" mean "I set a cap of 255", which is the
               * one thing an author clearing it demonstrably does not mean. `getEffectiveMaxLength`
               * in `@atlas-forms/player-components-react` reads `undefined` as "no cap", which is
               * also how `FormField.tsx` builds (or omits) the `maxLength` attribute.
               */
              onChange={(e) =>
                onConfigChange(
                  'maxLength',
                  e.target.value ? parseInt(e.target.value, 10) : undefined,
                )
              }
              className="af-properties-tab__input"
              placeholder="No limit"
            />
          </label>
          <p className="af-properties-tab__help">
            Optional. Leave blank for no limit — a text field enforces nothing until a value is set
            here. Once set, it is enforced by the field's own <code>maxLength</code> attribute while
            typing, and applied to values confirmed in the Expression Builder as well: a template
            longer than the limit is truncated to fit, exactly as typing past it is.
          </p>
        </div>
      </div>

      {/*
        `TEXT_EXPRESSION_DEFAULT_FORMAT` is the same constant `FormField.tsx`'s `case 'text':` passes
        `ExpressionBuilderTrigger`, imported rather than restated, so the "(default)" marker shown in
        the Output Format list cannot disagree with the fallback the runtime actually applies.
      */}
      <ExpressionBuilderSection
        control={control}
        onConfigChange={onConfigChange}
        defaultOutputFormat={TEXT_EXPRESSION_DEFAULT_FORMAT}
      />

      <OtherConfigSection control={control} />
    </>
  );
}

/**
 * TextEditor - Handles single-line text control configuration
 */
export const TextEditor: PropertyEditor = {
  type: 'text',
  label: 'Text Input',
  description: 'Configure max length and Expression Builder support for a single-line text control',
  render: renderTextEditor,
};
